/**
 * TerasuAnswerFlow - GBなび 従業員 週次回答画面 (gb-terasu-pulse Phase 3)
 * docs/mockups/12_モック_GBなび_画面遷移_v3.html の画面①②準拠。
 *
 * LINEチャット風UI(Web)。固定1問 → ネガティブ回答時のみ深掘り1問 → テーマ1問 → 完了。
 * 全問3択タップ。回答は最後に一括送信 (terasuApi.submitResponses)。
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  terasuApi, getCurrentWeekKey,
  type WeekQuestionsResponse, type TerasuQuestionDTO, type TerasuMultiLang,
} from '../api/terasuApi';
import { getStoredLanguage } from '../../questionnaire/utils/languageUtils';
import './TerasuAnswerFlow.scss';

interface ChatItem {
  kind: 'question' | 'reply' | 'feedback';
  text: string;
}

/** 保存言語に応じた設問/選択肢テキストを返す（未翻訳・ja は text へフォールバック）。 */
const LANG_FIELD: Record<string, keyof TerasuMultiLang> = {
  en: 'textEn', id: 'textId', mn: 'textMn', ne: 'textNe', vi: 'textVi',
  ar: 'textAr', bn: 'textBn', zh: 'textZh', es: 'textEs',
};
function pickText(o: { text: string } & Partial<TerasuMultiLang>): string {
  const lang = getStoredLanguage();
  if (lang === 'ja') return o.text;
  const f = LANG_FIELD[lang];
  const v = f ? o[f] : null;
  return typeof v === 'string' && v.length > 0 ? v : o.text;
}

interface TerasuAnswerFlowProps {
  /** 完了時に呼ばれる（embed: ネイティブへ ANSWER_DONE を送るのに使う）。 */
  onComplete?: () => void;
}

/** 表示する設問の並び (固定 → [深掘り] → テーマ) をステップ管理 */
export const TerasuAnswerFlow: React.FC<TerasuAnswerFlowProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  // 検証用に ?weekKey= で上書き可能。無ければ現在週
  const weekKey = useMemo(() => {
    const p = new URLSearchParams(window.location.search).get('weekKey');
    return p || getCurrentWeekKey();
  }, []);

  const [data, setData] = useState<WeekQuestionsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chat, setChat] = useState<ChatItem[]>([]);
  const [answers, setAnswers] = useState<Array<{ questionId: string; choiceIndex: number }>>([]);
  const [current, setCurrent] = useState<TerasuQuestionDTO | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    terasuApi.getWeekQuestions(weekKey)
      .then((res) => {
        setData(res);
        const first = res.fixed[0] ?? res.theme[0] ?? null;
        if (first) {
          setCurrent(first);
          setChat([{ kind: 'question', text: pickText(first) }]);
        }
        setIsLoading(false);
      })
      .catch((e) => {
        console.error('[Terasu] getWeekQuestions failed', e);
        setError(t('terasu.loadError'));
        setIsLoading(false);
      });
  }, [weekKey]);

  /** 次に表示する設問を決める: 固定→(negativeなら深掘り)→テーマ→終了 */
  const nextQuestion = useCallback((answered: TerasuQuestionDTO, choiceIndex: number): TerasuQuestionDTO | null => {
    if (!data) return null;
    if (answered.category === 'fixed') {
      const choice = answered.choices.find((c) => c.order === choiceIndex);
      // ネガティブ回答なら深掘り
      if (choice?.polarity === 'negative' && data.followups[0]) {
        return data.followups[0];
      }
      return data.theme[0] ?? null;
    }
    if (answered.category === 'followup') {
      return data.theme[0] ?? null;
    }
    return null; // theme 回答後は終了
  }, [data]);

  const handleChoice = useCallback((choiceIndex: number) => {
    if (!current || isSubmitting) return;
    const choice = current.choices.find((c) => c.order === choiceIndex);
    const newAnswers = [...answers, { questionId: current.id, choiceIndex }];
    setAnswers(newAnswers);
    setChat((prev) => [...prev, { kind: 'reply', text: choice ? pickText(choice) : '' }]);

    const next = nextQuestion(current, choiceIndex);
    setTimeout(() => {
      if (next) {
        setCurrent(next);
        setChat((prev) => [...prev, { kind: 'question', text: pickText(next) }]);
      } else {
        // 全問完了 → 送信
        submit(newAnswers);
      }
    }, 350);
  }, [current, answers, isSubmitting, nextQuestion]);

  const submit = useCallback(async (finalAnswers: typeof answers) => {
    setIsSubmitting(true);
    try {
      await terasuApi.submitResponses(weekKey, finalAnswers);
      setChat((prev) => [...prev, { kind: 'feedback', text: t('terasu.feedback') }]);
      setCurrent(null);
      setDone(true);
    } catch (e) {
      console.error('[Terasu] submit failed', e);
      setError(t('terasu.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [weekKey, t]);

  if (isLoading) {
    return <div className="terasu-answer"><div className="terasu-phone"><div className="terasu-content terasu-center">{t('terasu.loading')}</div></div></div>;
  }
  if (error) {
    return <div className="terasu-answer"><div className="terasu-phone"><div className="terasu-content terasu-center">{error}</div></div></div>;
  }

  return (
    <div className="terasu-answer">
      <div className="terasu-phone">
        <div className="terasu-topbar">
          <span className="terasu-logo">GBなび</span>
          <span className="terasu-topbar-sub">{data?.weekKey}</span>
        </div>
        <div className="terasu-content">
          {chat.map((item, i) => {
            if (item.kind === 'question') {
              return <div key={i} className="terasu-bubble">{item.text}</div>;
            }
            if (item.kind === 'reply') {
              return <div key={i} className="terasu-reply">{item.text}</div>;
            }
            return <div key={i} className="terasu-feedback">{item.text}</div>;
          })}

          {current && !done && (
            <div className="terasu-choices">
              {[...current.choices].sort((a, b) => a.order - b.order).map((c) => (
                <button
                  key={c.order}
                  type="button"
                  className="terasu-choice-btn"
                  disabled={isSubmitting}
                  onClick={() => handleChoice(c.order)}
                >
                  {pickText(c)}
                </button>
              ))}
            </div>
          )}

          {done && (
            <div className="terasu-done">
              <div className="terasu-done-check">✓</div>
              <div className="terasu-done-text">{t('terasu.doneTitle')}<br />{t('terasu.doneSubtitle')}</div>
              {onComplete && (
                <button type="button" className="terasu-choice-btn" onClick={() => onComplete()}>
                  {t('terasu.close')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TerasuAnswerFlow;
